import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  supportedCountries,
  supportedLocales,
} from '@/constants/supportedLocales';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function getLang(locale) {
  if (locale === 'fr-CA') {
    return 'fr-FR';
  }
  return locale;
}

export const getSafeLocaleUtil = (unsafeLocale) => {
  try {
    const matchingLocale = supportedLocales.find(
      (locale) => locale.toLowerCase() === unsafeLocale.toLowerCase()
    );
    if (matchingLocale) {
      return matchingLocale;
    }
    // eslint-disable-next-line no-empty -- just using try to cover multiple non-breaking cases
  } catch (e) {}
  return null;
};

export function getCountryCode(product, locale) {
  if (product?.includes('-idc')) {
    /*
     "country", in the case of this function, is used to tell the user that the content they're viewing -- for the 
     card they have selected -- is related to the international dollar card rather than one specic country.
    */
    return 'International Dollar Card';
  }
  const lowerCaseLocale = locale ? locale.toLowerCase() : 'en-us';
  const supportedCountry = supportedCountries.find(
    (country) => country.locale === lowerCaseLocale
  );

  // return country code if available, if not full country name
  return supportedCountry?.code || supportedCountry?.name || 'US';
}

export function getQueryParams(name) {
  if (typeof window === 'undefined') return null;

  const url = new URL(window.location.href);
  return url.searchParams.get(name);
}

export function getCardArtUrl({
  baseOneCmsImgUrl,
  product,
  locale,
  isMedium = false,
}) {
  // handle default case with no locale query param
  const medium = isMedium ? '-medium' : '';
  const imageLocale = locale || 'en-US';
  return `${baseOneCmsImgUrl}card-art/${product}_${imageLocale.toLocaleLowerCase()}${medium}.png`;
}

export function resizeSelectElementToCurrentValue(select) {
  const newSelect = select;
  // remove each <option> except the current value
  const selectValues = [];
  newSelect.querySelectorAll('option').forEach((option) => {
    selectValues.push(option);
    if (option.getAttribute('value') !== newSelect.value) {
      newSelect.removeChild(option);
    }
  });

  // allow the <select> to auto-size
  newSelect.style.width = null;

  // copy that width to "freeze" it
  newSelect.style.width = `${newSelect.clientWidth + 20}px`;

  // restore the <select>'s children
  newSelect.innerText = '';
  selectValues.forEach((option) => newSelect.appendChild(option));
  return newSelect;
}

export function isNewUrl(pathname) {
  const path =
    typeof pathname !== 'undefined'
      ? pathname
      : typeof window !== 'undefined'
        ? window.location?.pathname
        : null;
  return typeof path === 'string' ? path?.includes('/travel/lounges') : false;
}

export function getBasePathFromLocation(locale, pathname) {
  const currentBasePath = `/lounge-access`;
  const newBasePath = `/${locale.toLocaleLowerCase()}/travel/lounges`;

  return isNewUrl(pathname) ? newBasePath : currentBasePath;
}

export function getMarketName(locale) {
  const [lang = 'en', region = 'US'] = locale.split('-');
  const countryName = Intl.DisplayNames
    ? new Intl.DisplayNames([lang], {
        type: 'region',
        style: 'short',
      }).of(region.toUpperCase())
    : region;

  return countryName;
}

/**
 * Formats text by replacing variables in the text with their corresponding values.
 *
 * @param {string} text Text to format.
 * @param {object} variables Object containing variables to replace in the text.
 * @returns {string} Text with variables replaced.
 */
export function formatText(text, variables) {
  let formattedText = text;
  Object.keys(variables).forEach((key) => {
    formattedText = formattedText.replace(
      new RegExp(`{${key}}`, 'g'),
      variables[key]
    );
  });
  return formattedText;
}

export const setAXPLocaleCookie = (locale) => {
  if (!locale || typeof document === 'undefined') return;
  try {
    document.cookie = `axplocale=${locale}; path=/ ;domain=.americanexpress.com`;
  } catch (error) {
    // this can fail if the cookie is blocked
    // eslint-disable-next-line no-console -- helpful error in the terminal or console
    console.error(`setAXPLocaleCookie error: ${error?.message}`);
  }
};

/**
 * Returns the Lounge Finder product based on product type in the user/profile api response.
 *
 * @param {Array} types MYCA product types returned via the user profile api.
 * @returns {string} Lounge Finder product name.
 */
export const getProduct = (types) => {
  if (!Array.isArray(types)) {
    return 'the-platinum-card';
  }

  if (types.includes('Platinum')) {
    return 'the-platinum-card';
  }
  if (types.includes('Centurion')) {
    return 'the-centurion-card';
  }
  if (types.includes('DeltaReserve')) {
    return 'delta-reserve';
  }

  return 'the-platinum-card';
};
